import { defineStore } from 'pinia';
import type { DynamicComponent, SnackBanner } from '@/types';

const initDynamicComponent = (): DynamicComponent => ({
  value: false,
  component: undefined,
  data: {},
  events: {},
  animation: 'fade',
});

const initialSnackBanner: SnackBanner[] = [];

export const useUIStore = defineStore(
  'ui',
  () => {
    const dynamicSidePanel = ref<DynamicComponent>(initDynamicComponent());
    const dynamicPopup = ref<DynamicComponent>(initDynamicComponent());
    const snackBanners = ref<SnackBanner[]>([...initialSnackBanner]);

    // Counts active body-scroll locks (popups, modals, etc.). Lets floating
    // UI like the WhatsApp bubble step aside while a modal is open instead of
    // overlapping the modal content (the bubble is z-50 but the popup escapes
    // the document stacking context on some routes).
    const modalLockCount = ref(0);
    const isModalOpen = computed(() => modalLockCount.value > 0);
    const pushModalLock = () => {
      modalLockCount.value += 1;
    };
    const popModalLock = () => {
      modalLockCount.value = Math.max(0, modalLockCount.value - 1);
    };

    // "I'm with client" mode (#41). When ON, commission figures are hidden
    // across the app and revenue pages (/deals, /referrals, /earnings,
    // /deals-register*) redirect back to /explore with a toast.
    const clientMode = ref(false);
    const setClientMode = (value: boolean) => {
      clientMode.value = value;
    };
    const toggleClientMode = () => {
      clientMode.value = !clientMode.value;
    };

    // Auth prompt modal (#39). Flipped ON when an unauth user lands on a
    // project detail URL directly.
    const authPromptOpen = ref(false);
    const setAuthPromptOpen = (value: boolean) => {
      authPromptOpen.value = value;
    };

    const showSidePanel = (
      component: DynamicComponent['component'],
      data: object = {},
      events: object = {},
      animation = 'fade',
    ) => {
      dynamicSidePanel.value = {
        value: true,
        component: markRaw(component!),
        data,
        events,
        animation,
      };
    };

    const showPopup = (component: DynamicComponent['component'], data: object = {}, events: object = {}) => {
      if (!component) {
        console.warn('showPopup called with undefined component');
        return;
      }
      dynamicPopup.value = {
        value: true,
        component: markRaw(component),
        data: data || {},
        events: events || {},
      };
    };

    // Captured at store creation (always inside a Nuxt context) — showSnackBanner
    // is often called from async callbacks where useNuxtApp() can't resolve.
    const nuxtApp = tryUseNuxtApp();
    const defaultSnackText = (type: SnackBanner['type']): string => {
      if (type === 'success') return nuxtApp?.$i18n.t('shell.snackBanner.success') ?? 'Success';
      return nuxtApp?.$i18n.t('shell.snackBanner.error') ?? 'Something went wrong';
    };

    const showSnackBanner = (type: SnackBanner['type'], text: string) => {
      snackBanners.value.push({
        id: Date.now(),
        type,
        text: text || defaultSnackText(type),
      });
    };

    const resetSnackBanner = (id: number) => {
      snackBanners.value = snackBanners.value.filter((item) => item.id !== id);
    };

    const cleanSidePanel = () => {
      dynamicSidePanel.value = initDynamicComponent();
    };

    const cleanPopup = () => {
      dynamicPopup.value = initDynamicComponent();
    };

    const getDynamicSidePanel = computed(() => dynamicSidePanel.value);
    const getDynamicPopup = computed(() => dynamicPopup.value);

    return {
      dynamicSidePanel,
      dynamicPopup,
      snackBanners,
      clientMode,
      authPromptOpen,
      modalLockCount,
      isModalOpen,
      pushModalLock,
      popModalLock,
      setClientMode,
      toggleClientMode,
      setAuthPromptOpen,
      showSidePanel,
      showPopup,
      cleanSidePanel,
      getDynamicSidePanel,
      showSnackBanner,
      resetSnackBanner,
      cleanPopup,
      getDynamicPopup,
    };
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.localStorage(),
      pick: ['clientMode'],
    },
  },
);
